import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
    Card,
    Typography,
    Chip,
    Box,
    Button,
    IconButton,
    InputAdornment,
    TextField,
    MenuItem,
    Menu,
    Select,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import { Row, Col } from "react-bootstrap";
import { API_ENDPOINTS, getData } from "../../services/apiService.js";
import LoadingSpinner from "../LoadingContext.js";
import { useNavigate } from "react-router-dom";
import { encryptId } from "../helper/idCodec.js";
import "./Trips.css";

const TripsList = () => {
    const [trips, setTrips] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("");
    const [selectedDate, setSelectedDate] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const openFilterMenu = Boolean(anchorEl);

    const [currentPage, setCurrentPage] = useState(0);
    const [tripsPerPage, setTripsPerPage] = useState(10);
    const navigate = useNavigate();

    // Convert JS Date or dayjs to YYYY-MM-DD local string
    const formatDateToLocal = (date) => {
        if (!date) return "";
        const jsDate = date.toDate ? date.toDate() : date; // handle dayjs
        const year = jsDate.getFullYear();
        const month = String(jsDate.getMonth() + 1).padStart(2, "0");
        const day = String(jsDate.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    // ✅ Memoized fetchTrips to fix useEffect dependency warning
    const fetchTrips = useCallback(async (searchText = "", statusValue = "", dateValue = null) => {
        setLoading(true);
        try {
            const response = await getData(API_ENDPOINTS.allTrips, {
                search: searchText,
                status: statusValue,
                timeFilter: "all",
                actual_start_date_from: formatDateToLocal(dateValue),
                actual_start_date_to: formatDateToLocal(dateValue),
            });
            setTrips(response.result.trips);
        } catch (err) {
            console.error("Failed to fetch trips:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch trips on mount
    useEffect(() => {
        fetchTrips();
    }, [fetchTrips]);

    // --- Handlers ---
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearch(value);
        if (value.trim() === "") {
            setCurrentPage(0);
            fetchTrips("", status, selectedDate);
        }
    };

    const handleSearchKeyPress = (e) => {
        if (e.key === "Enter") {
            setCurrentPage(0);
            fetchTrips(search.trim(), status, selectedDate);
        }
    };

    const handleClearSearch = () => {
        setSearch("");
        setCurrentPage(0);
        fetchTrips("", status, selectedDate);
    };

    const handleFilterButtonClick = (e) => setAnchorEl(e.currentTarget);
    const handleFilterMenuClose = () => setAnchorEl(null);

    const handleStatusSelect = (label) => {
        let apiValue = "";
        if (label === "Active") apiValue = "active";
        else if (label === "In-Active") apiValue = "inactive";
        else if (label === "Completed") apiValue = "done";

        setStatus(apiValue);
        setAnchorEl(null);
        setCurrentPage(0);
        fetchTrips(search.trim(), apiValue, selectedDate);
    };

    const handleDateChange = (date) => {
        setSelectedDate(date);
        setCurrentPage(0);
        fetchTrips(search.trim(), status, date);
    };

    const handleClearDate = () => {
        setSelectedDate(null);
        setCurrentPage(0);
        fetchTrips(search.trim(), status, null);
    };

    // --- Pagination & memo ---
    const filteredTrips = useMemo(() => trips, [trips]);

    const paginatedTrips = useMemo(() => {
        const startIndex = currentPage * tripsPerPage;
        return filteredTrips.slice(startIndex, startIndex + tripsPerPage);
    }, [filteredTrips, currentPage, tripsPerPage]);

    const totalPages = Math.ceil(filteredTrips.length / tripsPerPage);

    const handleTripsPerPageChange = (event) => {
        setTripsPerPage(event.target.value);
        setCurrentPage(0);
    };

    const handlePageChange = (_, newPage) => setCurrentPage(newPage);

    return (
        <>
            <LoadingSpinner isLoading={loading} />

            {/* Control Bar */}
            <Row className="control-bar align-items-center">
                {/* Search */}
                <Col xs={12} md={7} lg={7} className="search-col mb-2 mb-md-0">
                    <TextField
                        fullWidth
                        placeholder="Search for ID, Truck ID, Truck Name, or City"
                        variant="outlined"
                        size="small"
                        value={search}
                        onChange={handleSearchChange}
                        onKeyDown={handleSearchKeyPress}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                            endAdornment: search && (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={handleClearSearch}>
                                        <ClearIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Col>

                {/* DatePicker + Filter */}
                <Col xs={12} md={5} lg={5} className="d-flex justify-content-end gap-2 mb-2 mb-md-0">
                    <Box display="flex" alignItems="center" gap={1} width="100%">
                        <DatePicker
                            label="Select Date"
                            value={selectedDate}
                            onChange={handleDateChange}
                            format="DD/MM/YYYY"
                            slotProps={{ textField: { size: "small", fullWidth: true } }}
                            className="dateFilter"
                        />
                        {selectedDate && (
                            <IconButton
                                color="error"
                                onClick={handleClearDate}
                                size="small"
                                title="Clear date filter"
                                sx={{ border: "1px solid #ccc" }}
                            >
                                <ClearIcon />
                            </IconButton>
                        )}
                    </Box>

                    <Button
                        variant="outlined"
                        startIcon={<FilterListIcon />}
                        onClick={handleFilterButtonClick}
                        aria-controls={openFilterMenu ? "filter-menu" : undefined}
                        aria-haspopup="true"
                        aria-expanded={openFilterMenu ? "true" : undefined}
                        className="filter-button"
                    >
                        Filter
                    </Button>

                    <Menu
                        id="filter-menu"
                        anchorEl={anchorEl}
                        open={openFilterMenu}
                        onClose={handleFilterMenuClose}
                    >
                        {["All", "Active", "In-Active", "Completed"].map((opt) => (
                            <MenuItem key={opt} onClick={() => handleStatusSelect(opt)}>
                                {opt}
                            </MenuItem>
                        ))}
                    </Menu>
                </Col>
            </Row>

            {/* Trips List */}
            {paginatedTrips.length > 0 ? (
                paginatedTrips.map((trip) => {
                    let statusLabel = "In-Active";
                    if (trip.trip_started && trip.trip_completed) statusLabel = "Completed";
                    else if (trip.trip_started && !trip.trip_completed) statusLabel = "Active";

                    const statusColor =
                        statusLabel === "Completed"
                            ? { bg: "rgba(224, 242, 254, 1)", color: "rgba(3, 105, 161, 1)" }
                            : statusLabel === "Active"
                                ? { bg: "rgb(222, 246, 230)", color: "rgb(10, 192, 94)" }
                                : { bg: "rgb(255, 240, 230)", color: "rgb(255, 107, 0)" };

                    return (
                        <Card
                            key={trip.id}
                            sx={{ borderRadius: 2, boxShadow: 2, mb: 1.5, maxWidth: "100%" }}
                            className="trip-card"
                        >
                            <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={2}>
                                <Box display="flex" alignItems="flex-start" gap={2} flexGrow={1}>
                                    <Box
                                        sx={{
                                            p: 1.2,
                                            borderRadius: "10px",
                                            backgroundColor: statusColor.bg,
                                            color: statusColor.color,
                                            display: { xs: "none", sm: "block" },
                                        }}
                                    >
                                        <LocalShippingIcon fontSize="large" />
                                    </Box>
                                    <Box>
                                        <Typography variant="h6" fontWeight="bold" className="trip-id">
                                            Trip ID: {trip.id}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            display="flex"
                                            color="text.secondary"
                                            my={0.5}
                                        >
                                            <img
                                                src="/smart-vectors/Location-Pin-Grey.svg"
                                                alt="location-pin"
                                                className="location-pin-vector"
                                            />
                                            {trip.trip_started_at} - {trip.trip_ended_at}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            display="flex"
                                            color="text.secondary"
                                            my={0.5}
                                        >
                                            <img
                                                src="/smart-vectors/Person-Grey.svg"
                                                alt="driver"
                                                className="location-pin-vector"
                                            />
                                            {trip.Drivers?.first_name} ({trip.Drivers?.driver_id})
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box display="flex" flexDirection="row" justifyContent="start" alignItems="start" columnGap={3}>
                                    <Box display="flex" flexDirection="row" justifyContent="start" alignItems="center" columnGap={4}>
                                        <Typography variant="body2" color="text.secondary">
                                            Start: {trip.actual_start_time ? new Date(trip.actual_start_time).toLocaleDateString() : "-"}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            End: {trip.reach_date ? new Date(trip.reach_date).toLocaleDateString() : "-"}
                                        </Typography>
                                    </Box>
                                    <Chip
                                        label={statusLabel}
                                        size="small"
                                        sx={{
                                            fontWeight: "bold",
                                            backgroundColor: statusColor.bg,
                                            color: statusColor.color,
                                        }}
                                        className="status-chip"
                                    />
                                </Box>
                            </Box>

                            <Box display="flex" justifyContent="space-between" mt={1} flexWrap="wrap" gap={2}>
                                <Box display="flex" gap={5}>
                                    <Typography variant="body2" color="text.secondary">
                                        Packages:
                                    </Typography>
                                    <Typography variant="body1" fontWeight="bold">
                                        {trip.total_packages}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" ml={4}>
                                        Revenue:
                                    </Typography>
                                    <Typography variant="body1" fontWeight="bold">
                                        {Number(trip.total_revenue).toFixed(2)}
                                    </Typography>
                                </Box>
                                <Button
                                    variant="text"
                                    size="small"
                                    endIcon={<ArrowRightAltIcon />}
                                    onClick={() => navigate(`/app/trip-details/${encryptId(trip.id)}`)}
                                >
                                    View Details
                                </Button>
                            </Box>
                        </Card>
                    );
                })
            ) : (
                <Typography variant="body1" color="text.secondary" align="center" sx={{ mt: 4 }}>
                    No Data Found
                </Typography>
            )}

            {/* Pagination */}
            {filteredTrips.length > 0 && (
                <Box className="pagination-controls" display="flex" justifyContent="end" alignItems="center" mt={2}>
                    <div className="items-per-page" style={{ display: "flex", alignItems: "center" }}>
                        <Select value={tripsPerPage} onChange={handleTripsPerPageChange} size="small">
                            {[5, 10, 25, 50].map((opt) => (
                                <MenuItem key={opt} value={opt}>
                                    {opt}
                                </MenuItem>
                            ))}
                        </Select>
                        <Typography variant="body2" sx={{ ml: 1 }}>
                            per page
                        </Typography>
                    </div>

                    <Typography variant="body2" sx={{ mx: 2 }}>
                        {currentPage + 1} of {totalPages} pages
                    </Typography>

                    <IconButton
                        onClick={() => handlePageChange(null, currentPage - 1)}
                        disabled={currentPage === 0}
                    >
                        <KeyboardArrowLeft />
                    </IconButton>
                    <IconButton
                        onClick={() => handlePageChange(null, currentPage + 1)}
                        disabled={currentPage >= totalPages - 1}
                    >
                        <KeyboardArrowRight />
                    </IconButton>
                </Box>
            )}
        </>
    );
};

export default TripsList;
